import React from "react";
import { useSidebarStore } from "../SidebarStore";
import { useSelf } from "@/hooks/useSelf";

export type SidebarExpansionOptions = {
  collapseSidebarOnBreakpoint: number;
};

export function useSidebarExpansion(
  options: SidebarExpansionOptions = { collapseSidebarOnBreakpoint: 768 },
) {
  const { collapseSidebarOnBreakpoint } = options;
  const self = useSelf();
  const userId = self.data?.id ?? "anonymous";

  const isExpanded = useSidebarStore((state) => state.isExpanded[userId] !== false);
  const setIsExpanded = useSidebarStore((state) => state.setIsExpanded);

  const userToggleRef = React.useRef(false);

  const toggleExpansion = React.useCallback(() => {
    userToggleRef.current = true;
    setIsExpanded(userId, !isExpanded);
  }, [userId, isExpanded, setIsExpanded]);

  React.useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (userToggleRef.current) {
        return;
      }

      if (window.innerWidth < collapseSidebarOnBreakpoint) {
        setIsExpanded(userId, false);
      }
    });

    resizeObserver.observe(document.body);
    return () => {
      resizeObserver.unobserve(document.body);
      resizeObserver.disconnect();
    };
  }, [collapseSidebarOnBreakpoint, userId, setIsExpanded]);

  return {
    isSidebarExpanded: isExpanded,
    toggleExpansion,
  };
}
